import React, { useState, useEffect, useRef } from 'react';
import clsx from '@strava/ui/clsx';
import Button from '@strava/ui/Button';
import NavigationMenuNormalLarge from '@strava/icons/NavigationMenuNormalLarge';
import ActionsCancelNormalLarge from '@strava/icons/ActionsCancelNormalMedium';
import Drawer from 'react-modern-drawer';
import StravaLogo from '@strava/ui/StravaLogo';

import { getMobileOS } from '@strava/utils';
import SmartBanner from '@strava/smart-banner-ui';
import { useFrontDoorsPageContainerContext } from '../../../../state/FrontDoorsPageContainerContext';
import styles from './MobileNav.module.scss';
import 'react-modern-drawer/dist/index.css';
import IconButton from '../../../IconButton';

import CallToAction from '../../../CallToAction';

const MobileNav = () => {
  const [isMenuOpen, toggleMenu] = useState(false);
  const mobileAppDownloadBannerRef = useRef();
  const { t, track, state } = useFrontDoorsPageContainerContext();
  const {
    page,
    category,
    userAgent,
    showSmartBanner,
    branch,
    url,
    header = {}
  } = state;
  const [forceSmartBanner, setForceSmartbanner] = useState(false);
  const { analyticsProperties } = header;

  const handleLoginBtnClick = () => {
    track({
      element: 'drawer_login_btn',
      action: 'click',
      ...(analyticsProperties && { properties: analyticsProperties })
    });
    window.location.href = '/login';
  };

  const handleLogoClick = () => {
    track({
      element: 'strava_logo',
      action: 'click',
      ...(analyticsProperties && { properties: analyticsProperties })
    });
    window.location.href = '/';
  };

  // disables scroll when the menu is open
  useEffect(() => {
    if (!document?.body?.style) {
      return () => {};
    }
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'scroll';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = 'scroll';
    };
  }, [isMenuOpen]);

  const handleMenuToggle = () => {
    track({
      element: isMenuOpen ? 'close_menu' : 'open_menu',
      action: 'click',
      ...(analyticsProperties && { properties: analyticsProperties })
    });
    toggleMenu(!isMenuOpen);
  };

  // For development only - use query param to force the smartbanner to show
  // on a page.
  useEffect(() => {
    if (window?.location?.href) {
      const [, queryParams] = window.location.href.split('?');
      const searchParams = new URLSearchParams(queryParams);
      setForceSmartbanner(!!searchParams.get('force_smartbanner'));
    }
  }, [url]);

  return (
    <>
      {(showSmartBanner || forceSmartBanner) && (
        <SmartBanner
          mobileOS={getMobileOS(userAgent)}
          page={page}
          category={category}
          branch={branch}
          ref={mobileAppDownloadBannerRef}
        />
      )}
      <div className={styles.container}>
        <div className={styles.left}>
          <IconButton
            className={clsx(styles.menuBtn, isMenuOpen && styles.isOpen)}
            onClick={handleMenuToggle}
            ariaLabel={
              isMenuOpen ? t('header.alt.close') : t('header.alt.close')
            }
            data-cy="mobile-nav-drawer-menu-btn"
          >
            {isMenuOpen ? (
              <ActionsCancelNormalLarge />
            ) : (
              <NavigationMenuNormalLarge />
            )}
          </IconButton>
          <div className={styles.logo}>
            <IconButton onClick={handleLogoClick} ariaLabel="Strava">
              <StravaLogo data-cy="strava-logo" width={95} height={20} alt="" />
            </IconButton>
          </div>
        </div>

        <div className={styles.right}>
          <CallToAction
            element="get_app"
            className={styles.headerCallToAction}
            label={t('header.get_the_app')}
            dataCy="signup_btn_mobile"
            analyticsProperties={{
              cta_copy: 'get_the_app_header',
              ...(analyticsProperties || {})
            }}
          />
        </div>
        <Drawer
          open={isMenuOpen}
          direction="top"
          zIndex={70}
          size={300}
          className={styles.menuDrawer}
          customIdSuffix="_strava_mobile_menu"
        >
          <Button
            type="button"
            onClick={() => handleLoginBtnClick()}
            className={styles.menuDrawerBtn}
            data-cy="mobile-nav-drawer-login-btn"
          >
            {t('header.log_in')}
          </Button>
          <CallToAction
            element="get_app"
            className={styles.drawerCallToAction}
            label={t('header.get_the_app')}
            dataCy="mobile-nav-drawer-signup-btn"
            analyticsProperties={{
              cta_copy: 'get_the_app_drawer',
              ...(analyticsProperties || {})
            }}
          />
        </Drawer>
      </div>
    </>
  );
};

export default MobileNav;
